<template>
    <form @submit.prevent="handleSubmit" v-if="employee" autocomplete="off">
        <section>
            <div class="flex align-items-center justify-content-between mb-3">
                <div>
                    <h3 class="m-0">Required Information</h3>
                    <p class="text-xs text-400 mt-1 mb-0">Employee ID is permanent and cannot be changed.</p>
                </div>
                <span class="text-xs text-400"><span class="text-red-500">*</span> Required fields</span>
            </div>

            <div class="grid">
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_id" class="block text-xs text-gray-400 mb-1">Employee ID</label>
                    <InputText id="edit_employee_id" :modelValue="employee.employee_id" disabled class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_name" class="block text-xs text-gray-400 mb-1">Name <span class="text-red-500">*</span></label>
                    <InputText id="edit_employee_name" v-model="employee.employee_name" :disabled="isUpdating" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_email" class="block text-xs text-gray-400 mb-1">Email <span class="text-red-500">*</span></label>
                    <InputText id="edit_employee_email" type="email" v-model="employee.employee_email" :disabled="isUpdating" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_badge_id" class="block text-xs text-gray-400 mb-1">Badge ID <span class="text-red-500">*</span></label>
                    <InputText id="edit_employee_badge_id" v-model="employee.employee_badge_id" :disabled="isUpdating" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_badge_pin" class="block text-xs text-gray-400 mb-1">Badge PIN <span class="text-red-500">*</span></label>
                    <InputText id="edit_employee_badge_pin" v-model="employee.employee_badge_pin" :disabled="isUpdating" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_status" class="block text-xs text-gray-400 mb-1">Status <span class="text-red-500">*</span></label>
                    <Dropdown id="edit_employee_status" v-model="employee.employee_status" :options="statusOptions" :disabled="isUpdating" optionLabel="name" optionValue="code" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_department" class="block text-xs text-gray-400 mb-1">Department <span class="text-red-500">*</span></label>
                    <Dropdown id="edit_employee_department" v-model="employee.employee_department" :options="departmentOptions" :disabled="isUpdating" optionLabel="name" optionValue="code" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_tenant_id" class="block text-xs text-gray-400 mb-1">Tenant <span class="text-red-500">*</span></label>
                    <Dropdown id="edit_tenant_id" v-model="employee.tenant_id" :options="tenantOptions" :disabled="isUpdating" optionLabel="name" optionValue="code" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_company_id" class="block text-xs text-gray-400 mb-1">Company <span class="text-red-500">*</span></label>
                    <Dropdown id="edit_employee_company_id" v-model="employee.employee_company_id" :options="companiesList" :disabled="isUpdating" optionLabel="company_name" optionValue="company_id" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_contract" class="block text-xs text-gray-400 mb-1">Contract <span class="text-red-500">*</span></label>
                    <Dropdown id="edit_employee_contract" v-model="employee.employee_contract" :options="contractOptions" :disabled="isUpdating" optionLabel="name" optionValue="code" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_join_date" class="block text-xs text-gray-400 mb-1">Join Date <span class="text-red-500">*</span></label>
                    <Calendar id="edit_employee_join_date" dateFormat="yy-mm-dd" v-model="employee.employee_join_date" :disabled="isUpdating" :showIcon="true" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_contract_date" class="block text-xs text-gray-400 mb-1">Contract Date <span class="text-red-500">*</span></label>
                    <Calendar id="edit_employee_contract_date" dateFormat="yy-mm-dd" v-model="employee.employee_contract_date" :disabled="isUpdating" :showIcon="true" class="w-full" />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_extra_bonuses" class="block text-xs text-gray-400 mb-1">Extra Bonuses</label>
                    <MultiSelect id="edit_employee_extra_bonuses" v-model="employee.employee_extra_bonuses" :options="bonusOptions" :disabled="isUpdating" optionLabel="name" optionValue="code" display="chip" class="w-full" @change="handleBonusChange" :optionDisabled="option => option.code === 'NONE'" />
                </div>
            </div>
        </section>

        <details class="optional-section mt-3">
            <summary class="cursor-pointer font-semibold py-3 px-3">
                Optional Information
                <span class="block text-xs text-400 font-normal mt-1">Photo, contact, identity and family details</span>
            </summary>
            <div class="grid px-2 pt-3">
                <div class="col-12 md:col-4 mb-2">
                    <img :src="employee.employee_photo_url || defaultUserImage" class="w-full max-w-12rem h-12rem object-cover mb-2 rounded-md shadow-md" alt="Employee photo" />
                    <FileUpload mode="basic" accept="image/*" :disabled="isUpdating" :maxFileSize="3000000" @select="handleFileSelect" :auto="false" chooseLabel="Upload Photo" class="w-full max-w-12rem" />
                </div>
                <div class="col-12 md:col-8">
                    <div class="grid">
                        <div class="col-12 md:col-6 mb-1">
                            <label for="edit_employee_phone_number_main" class="block text-xs text-gray-400 mb-1">Phone Number (Main)</label>
                            <InputText id="edit_employee_phone_number_main" v-model="employee.employee_phone_number_main" :disabled="isUpdating" class="w-full" />
                        </div>
                        <div class="col-12 md:col-6 mb-1">
                            <label for="edit_employee_phone_number_secondary" class="block text-xs text-gray-400 mb-1">Phone Number (Secondary)</label>
                            <InputText id="edit_employee_phone_number_secondary" v-model="employee.employee_phone_number_secondary" :disabled="isUpdating" class="w-full" />
                        </div>
                        <div class="col-12 md:col-6 mb-1">
                            <label for="edit_employee_dob" class="block text-xs text-gray-400 mb-1">Date of Birth</label>
                            <Calendar id="edit_employee_dob" dateFormat="yy-mm-dd" v-model="employee.employee_dob" :disabled="isUpdating" :showIcon="true" class="w-full" />
                        </div>
                        <div class="col-12 md:col-6 mb-1">
                            <label for="edit_employee_gender" class="block text-xs text-gray-400 mb-1">Gender</label>
                            <Dropdown id="edit_employee_gender" v-model="employee.employee_gender" :options="genderOptions" :disabled="isUpdating" optionLabel="name" optionValue="code" showClear class="w-full" />
                        </div>
                        <div class="col-12 md:col-6 mb-1">
                            <label for="edit_employee_father_name" class="block text-xs text-gray-400 mb-1">Father's Name</label>
                            <InputText id="edit_employee_father_name" v-model="employee.employee_father_name" :disabled="isUpdating" class="w-full" />
                        </div>
                        <div class="col-12 md:col-6 mb-1">
                            <label for="edit_employee_cnic" class="block text-xs text-gray-400 mb-1">CNIC</label>
                            <InputText id="edit_employee_cnic" v-model="employee.employee_cnic" :disabled="isUpdating" class="w-full" />
                        </div>
                        <div class="col-12 md:col-6 mb-1">
                            <label for="edit_employee_next_of_kin" class="block text-xs text-gray-400 mb-1">Next of Kin</label>
                            <InputText id="edit_employee_next_of_kin" v-model="employee.employee_next_of_kin" :disabled="isUpdating" class="w-full" />
                        </div>
                        <div class="col-12 md:col-6 mb-1">
                            <label for="edit_employee_next_of_kin_number" class="block text-xs text-gray-400 mb-1">Next of Kin Number</label>
                            <InputText id="edit_employee_next_of_kin_number" v-model="employee.employee_next_of_kin_number" :disabled="isUpdating" class="w-full" />
                        </div>
                    </div>
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_address_permanent" class="block text-xs text-gray-400 mb-1">Permanent Address</label>
                    <Textarea id="edit_employee_address_permanent" v-model="employee.employee_address_permanent" :disabled="isUpdating" rows="3" class="w-full" autoResize />
                </div>
                <div class="col-12 md:col-6 mb-1">
                    <label for="edit_employee_address_current" class="block text-xs text-gray-400 mb-1">Current Address</label>
                    <Textarea id="edit_employee_address_current" v-model="employee.employee_address_current" :disabled="isUpdating" rows="3" class="w-full" autoResize />
                </div>
            </div>
        </details>
    </form>

    <!-- Loading and Error States -->
    <div v-else-if="loading" class="h-10rem">
        <div class="imab-logo-animated" style="left: 30%; top: 10%;">
            <div><span class="I">I</span><span class="M">M</span><span class="A">A</span><span class="B">B</span></div>
        </div>
    </div> 
    <p v-else-if="error">{{ error }}</p>

    <!-- Footer -->
    <footer class="flex overflow-hidden mt-3">
        <div class="flex-shrink-0 flex align-items-start justify-content-start mr-2 mt-1">
            <Button 
                label="Cancel" 
                :disabled="isUpdating || isUploading" 
                class="surface-200 hover:surface-400" 
                @click="handleBack" 
                aria-label="Cancel and go back" 
            />
        </div>
        <div class="flex-grow-1 flex align-items-start justify-content-end">
            <Button 
                label="Edit Employee" 
                :disabled="loading || isUpdating || isUploading" 
                :loading="isUpdating || isUploading" 
                class="w-full bg-primary hover:surface-900 px-3 mt-1" 
                @click="handleSubmit" 
                aria-label="Submit" 
            />
        </div>
    </footer>
</template>

<script setup>
import { onMounted, defineProps, defineEmits, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useEmployeeSingle } from '@/composables/api/useEmployeeSingle';
import { useEmployeeSingleUpdate } from '@/composables/api/useEmployeeSingleUpdate';
import { useCompaniesStore } from '@/stores/companies';
import { useStructuresStore } from '@/stores/structures';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import Textarea from 'primevue/textarea';
import FileUpload from 'primevue/fileupload';
import { useFileUpload } from '@/composables/useFileUpload';
import { storage } from '@/firebaseConfig';
import { deleteObject } from 'firebase/storage';
import { ref as storageRef } from 'firebase/storage';
import defaultUserImage from '@/assets/default-user.png';
import MultiSelect from 'primevue/multiselect';

const props = defineProps({
    selectedId: {
        type: Number,
        required: true
    }
});

const emit = defineEmits(['handleBack', 'employeeUpdated']);

const companiesStore = useCompaniesStore();
const structuresStore = useStructuresStore();
const { companiesList } = storeToRefs(companiesStore);
const { structuresList: contractOptions } = storeToRefs(structuresStore);
const { employee, loading, error, fetchEmployeeById } = useEmployeeSingle();
const { isUpdating, updateEmployee } = useEmployeeSingleUpdate();
const toast = useToast();
const { uploadFile, isUploading } = useFileUpload();

const statusOptions = [   
  { name: 'Permanent', code: 'Permanent' },
  { name: 'Probation', code: 'Probation' },
  { name: 'Trainee', code: 'Trainee' },
  { name: 'Resigned', code: 'Resigned' },
  { name: 'Terminated', code: 'Terminated' },
  { name: 'On Hold', code: 'On Hold' },
  { name: 'Cancelled', code: 'Cancelled' }
];

const tenantOptions = [   
  { name: 'ORION', code: 'orion-6sgo6' },
  { name: 'IOT Expert', code: 'iotexpert-jyn65' },
  { name: 'Datacom', code: 'datacom-8rz4d' }
];

const departmentOptions = [   
  { name: 'CSR Staff', code: 'HR / Operator' },
  { name: 'Covering Staff', code: 'HR / Shift Cover' },
  { name: 'House keeping', code: 'House Keeping' },
  { name: 'Tech Staff', code: 'Tech' },
  { name: 'Manager', code: 'Manager' },
  { name: 'Maintenance', code: 'Maintenance' }
];

const genderOptions = [
    { name: 'Male', code: 'Male' },
    { name: 'Female', code: 'Female' },
    { name: 'Other', code: 'Other' }
];

const bonusOptions = [
    { name: 'Travel', code: 'TRAVEL' },
    { name: 'Dispatch', code: 'DISPATCH' },
    { name: 'Team Lead', code: 'LEAD' },
    { name: 'Extra', code: 'EXTRA' },
    { name: 'NONE', code: 'NONE' }
];

const selectedFile = ref(null);

const formatDateForAPI = (date) => {
    if (!date) return null;
    if (typeof date === 'string') return date;
    
    // Get year, month and day components and create a YYYY-MM-DD string
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
};

const parseAPIDate = (dateString) => {
    if (!dateString) return null;
    
    // Split the date string and create a new date using components
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day);
};

onMounted(() => {
    fetchEmployeeById(props.selectedId);
});

const handleBack = () => {
    emit('handleBack');
};

const handleSubmit = async () => {
    const requiredFields = [
        'employee_name',
        'employee_email',
        'tenant_id',
        'employee_badge_id',
        'employee_badge_pin',
        'employee_department',
        'employee_company_id',
        'employee_status',
        'employee_join_date',
        'employee_contract_date',
        'employee_contract'
    ];

    const missingFields = requiredFields.filter(field => !employee.value[field]);

    if (missingFields.length > 0) {
        toast.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: `Please fill in all required fields: ${missingFields.join(', ')}`,
            life: 5000,
            group: 'br'
        });
        return;
    }

    try {
        if (selectedFile.value) {
            const fileExtension = selectedFile.value.name.split('.').pop();
            const path = `employee-photos/${props.selectedId}.${fileExtension}`;
            
            if (employee.value.photo_url) {
                try {
                    const oldRef = storageRef(storage, `employee-photos/${props.selectedId}.*`);
                    await deleteObject(oldRef);
                } catch (error) {
                    console.warn('Failed to delete old photo:', error);
                }
            }
            
            const photoUrl = await uploadFile(selectedFile.value, path);
            employee.value.employee_photo_url = photoUrl;
        }

        const changedFields = {
            employee_id: props.selectedId,
            employee_join_date: formatDateForAPI(employee.value.employee_join_date),
            employee_contract_date: formatDateForAPI(employee.value.employee_contract_date)
        };

        for (const [key, value] of Object.entries(employee.value)) {
            if (key !== 'employee_id' && 
                key !== 'employee_join_date' && 
                key !== 'employee_contract_date' && 
                JSON.stringify(value) !== JSON.stringify(initialEmployeeData.value[key])) {
                changedFields[key] = value;
            }
        }

        if (Object.keys(changedFields).length === 1) {
            toast.add({
                severity: 'info',
                summary: 'No Changes',
                detail: 'No changes were made to the employee data.',
                life: 3000,
                group: 'br'
            });
            return;
        }

        const updatedEmployee = await updateEmployee(changedFields);
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Employee updated successfully',
            life: 3000,
            group: 'br'
        });
        
        selectedFile.value = null;
        
        initialEmployeeData.value = JSON.parse(JSON.stringify(employee.value));
        emit('employeeUpdated', updatedEmployee);
    } catch (err) {
        let errorMessage = 'An error occurred while updating the employee';
        if (err.response && err.response.data) {
            if (err.response.status === 409) {
                errorMessage = `${err.response.data.error} Field: ${err.response.data.field}`;
            } else {
                errorMessage = err.response.data.error || errorMessage;
            }
        }
        toast.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: errorMessage,
            life: 5000,
            group: 'br'
        });
    }
};

const handleFileSelect = (event) => {
    selectedFile.value = event.files[0];
};

const handleBonusChange = (event) => {
    if (!event.value || event.value.length === 0) {
        employee.value.employee_extra_bonuses = ['NONE'];
    }
};

const initialEmployeeData = ref(null);

watch(employee, (newValue) => {
    if (newValue && !initialEmployeeData.value) {
        if (typeof newValue.employee_extra_bonuses === 'string') {
            newValue.employee_extra_bonuses = newValue.employee_extra_bonuses
                ? newValue.employee_extra_bonuses.split(',')
                : ['NONE'];
        }
        
        if (newValue.employee_join_date) {
            newValue.employee_join_date = parseAPIDate(newValue.employee_join_date);
        }
        if (newValue.employee_contract_date) {
            newValue.employee_contract_date = parseAPIDate(newValue.employee_contract_date);
        }
        initialEmployeeData.value = JSON.parse(JSON.stringify(newValue));
    }
}, { immediate: true });
</script>

<style scoped>
.employee-photo {
    object-fit: cover;
    border-radius: 4px;
}

.optional-section {
    border: 1px solid var(--p-content-border-color);
    border-radius: var(--p-content-border-radius);
}

.optional-section[open] summary {
    border-bottom: 1px solid var(--p-content-border-color);
}
</style>
